import { Dispatch } from 'redux';
import CrudActionContainer from './CrudActionContainer';
import InviteModel from '../model/InviteModel';
import InvitesController from '../controllers/InvitesController';
import commonHandler from '../errorHandlers/common-handler';
import companyCRUDActions from './CompanyCRUDActions';
import Actions from './actions';
import { UNAUTHORIZED, NO_SEATS } from '@mm/constants/status-codes';
import { StoreState } from '../store-interfaces';
import modalActions from '../modules/modalsState/actions';
import { NETWORK_ERROR_MODAL_TITLE } from '@mm/constants/error-descriptions';
import { getErrorJson } from '@mm/utils';
import { getActionHookBuilder } from './action-hook-builder';
import somethingWentWrong from 'assets/something-went-wrong.png';

const PERMISSIONS_ERROR_MESSAGE = `
  You do not have sufficient permissions to complete this action.
  Please contact the page owner for assistance.
`;

class InvitesCRUDActions extends CrudActionContainer {
  setAvailableSeatsAction: string;
  constructor() {
    super('INVITES');
    this.setAvailableSeatsAction = super.makeActionKey('SET_AVAILABLE_SEATS');
  }

  setAvailableSeatsActionCreator = (seats: number[]) => ({
    type: this.setAvailableSeatsAction,
    seats,
  });

  retrieveAll(companyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const response = await InvitesController.getAll(companyId);
        dispatch(this.retrievedAll(response));

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  create(companyId: number, invite: Partial<InviteModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await InvitesController.create(companyId, invite);
        dispatch(this.retrieved(response));
        return response;
      } catch (error) {
        const responseErrorBody = await getErrorJson(error);
        // Permissions error
        if (error instanceof Response && error.status === UNAUTHORIZED) {
          dispatch(
            modalActions.openPrettyErrorModal({
              errorMessage: '',
              title: NETWORK_ERROR_MODAL_TITLE,
              detailsForNerds: PERMISSIONS_ERROR_MESSAGE,
              contactSupport: true,
              image: somethingWentWrong,
            }),
          );
        } else if (responseErrorBody.error_code === NO_SEATS) {
          dispatch(
            modalActions.openGeneralTextModal({
              header: 'You have exceeded the number of available seats.',
              message: 'Please buy more seats in order to invite new users',
            }),
          );
        } else {
          commonHandler(dispatch)(error);
        }
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  update(invite: Partial<InviteModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await InvitesController.update(invite);
        dispatch(this.retrievedAll(response));
        return response;
      } catch (e) {
        dispatch(this.unbusy());
        commonHandler(dispatch)(e);
      }
    };
  }

  accept(inviteToken: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(companyCRUDActions.retrieveAll());
        return {};
      } catch (e) {
        dispatch({ type: Actions.UNBUSY });
        return e;
      }
    };
  }

  getAvailableSeats(companyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await InvitesController.getAvailableSeats(companyId);
        dispatch(this.setAvailableSeatsActionCreator(response));
        dispatch({ type: Actions.UNBUSY });
      } catch (e) {
        dispatch({ type: Actions.UNBUSY });
        return e;
      }
    };
  }

  delete(invite: InviteModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));
      try {
        const res = await InvitesController.destroy(invite);
        dispatch(this.deleteLocal(invite));
        dispatch(this.unbusy());
        return res;
      } catch (e) {
        dispatch(this.unbusy());

        if (e?.status === 422) {
          dispatch(
            modalActions.openPrettyErrorModal({
              title: 'Delete Invite',
              errorMessage:
                'The Invite is already accepted and can not be destroyed',
            }),
          );
          return e;
        }

        commonHandler(dispatch)(e);
        return e;
      }
    };
  }
}

export const invitesCRUDActions = new InvitesCRUDActions();

export const useInvitesCRUDActions = getActionHookBuilder(invitesCRUDActions, [
  'create',
  'retrieveAll',
  'update',
  'delete',
  'getAvailableSeats',
  'accept',
]);
